import {
  Expense,
  ExpenseMode,
  ExpenseStatus,
} from '../../../../interfaces/delivery-company/expenses';


import { Decimal } from 'decimal.js';
import { add, displayMoney, Money, moneyToMetric } from '../../../../utils/money';


export const emptyExpense: Expense = {
  id: 0,
  expense: [],
  mode: ExpenseMode.MANUAL,
  status: ExpenseStatus.PENDING,
  description: '',
};

export interface PieSlice {
  id: string;
  label: string;
  value: number;
  color: string;
  formattedValue: string;
}


const THEME_HUES = {
  orange:10, // softer orange
  green: 100, // more natural green
} as const;

export function generatePieData(
  data: [string, Money[]][],
  theme: keyof typeof THEME_HUES,
  tinySliceThreshold = 0.03
): PieSlice[] {
  const hue = THEME_HUES[theme];

  // Step 1: Map to [label, money[], metricValue]
  const metricData: [string, Money[], Decimal][] = data.map(([label, money]) => [
    label,
    money,
    moneyToMetric(money),
  ]);

  // Step 2: Total value
  const total = metricData.reduce((acc, [, , val]) => acc.add(val), new Decimal(0));

  // Step 3: Split major and minor slices
  const majorSlices: typeof metricData = [];
  let othersTotal: Money[] = [];

  for (const [label, money, value] of metricData) {
    const fraction = value.dividedBy(total);
    if (fraction.lessThan(tinySliceThreshold)) {
      othersTotal = add(othersTotal, money);
    } else {
      majorSlices.push([label, money, value]);
    }
  }

  // Step 4: Sort descending by importance
  majorSlices.sort((a, b) => b[2].comparedTo(a[2]));

  const count = majorSlices.length;
  const minLightness = 40; // darkest
  const maxLightness = 80; // lightest

  // Step 5: Build slices with dynamic HSL colors
  const slices: PieSlice[] = majorSlices.reverse().map(([label, money, value], index) => {
    const lightness = maxLightness - ((index / Math.max(1, count - 1)) * (maxLightness - minLightness));
    const color = `hsl(${hue}, 85%, ${lightness}%)`;

    return {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      value: value.toNumber(),
      formattedValue: displayMoney(money),
      color,
    };
  });

  // Step 6: Add "Others" if needed
  const othersMetric = moneyToMetric(othersTotal);
  if (!othersMetric.isZero()) {
    slices.push({
      id: 'others',
      label: 'Others',
      value: othersMetric.toNumber(),
      formattedValue: displayMoney(othersTotal),
      color: `hsl(${hue}, 85%, ${minLightness}%)`, // darkest shade
    });
  }

  return slices;
}
